import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, MapPin, Users, Heart, ChevronRight } from "lucide-react";
import { Reveal, Overline } from "./motion";
import { useTranslate } from "../context/TranslateContext";

const COMMUNITY_IMG =
  "https://images.pexels.com/photos/31715350/pexels-photo-31715350.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const FEATURES = [
  { icon: Heart, title: "My Cinema", body: "Track what you've watched, build watchlists and shape your own cinema identity." },
  { icon: Trophy, title: "CinemaPulse", body: "Earn points and badges for reviews, corrections and film knowledge." },
  { icon: MapPin, title: "Film Locations", body: "Discover where iconic scenes were shot and plan a trip to visit them." },
  { icon: Users, title: "Community", body: "Follow artists, join fan communities and share your recommendations." },
];

export const Community = () => {
  const { t } = useTranslate();
  const listenText = "Build your cinema identity. Track everything you watch, earn CinemaPulse points, follow your favourite artists and connect with fans across the world.";

  return (
    <section id="community" className="relative overflow-hidden border-t border-edge py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-edge">
              <div className="aspect-[4/5] md:aspect-square">
                <img src={COMMUNITY_IMG} alt="A bustling cinema-loving crowd" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/30" />
              </div>
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-edge bg-paper/85 p-5 backdrop-blur">
                <div className="font-display text-3xl text-gradient-gold">Malayalam Cinema Expert</div>
                <div className="font-body text-sm text-ash">{t("One of 40+ badges to earn as you contribute")}</div>
              </div>
            </div>
          </Reveal>

          <div>
            <Overline>Community · My Cinema</Overline>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight text-cream md:text-6xl">
              {t("Make it ")}<span className="italic text-gradient-gold">{t("yours")}</span>
            </h2>
            <p className="mt-6 font-body text-lg leading-relaxed text-ash">
              {t("This isn't a place you just read — it's one you belong to. Curate, contribute and connect with a global community of Indian cinema lovers.")}
            </p>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {FEATURES.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.05}>
                  <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.3 }}
                    data-testid={`community-feature-${i}`}
                    className="h-full rounded-2xl border border-edge bg-surface/40 p-5">
                    <f.icon className="h-6 w-6 text-gold" />
                    <h3 className="mt-3 font-display text-xl text-cream">{t(f.title)}</h3>
                    <p className="mt-1 font-body text-sm leading-relaxed text-ash">{t(f.body)}</p>
                  </motion.div>
                </Reveal>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              
              <Link to="/article/community-overview" data-testid="read-community-guide"
                className="inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-body text-sm text-gold transition-colors hover:bg-gold/10">
                {t("Read: Our Community Vision")} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
